import { getSiteConfig } from '@shared/config';
import { z } from 'zod';
import { ConfirmationStep } from '../components/steps/ConfirmationStep';
import { EditMobileNumberStep } from '../components/steps/EditMobileNumberStep';
import { VerificationStep } from '../components/steps/VerificationStep';

type EditMobileNumberFlowMode = 'add' | 'edit';

const createEditMobileNumberSteps = (
  registerBackHandler: (handler: () => boolean) => void,
  t: (key: string) => string,
  onConfirmDone: () => void,
  onVerificationFailureDone: () => void,
  mode: EditMobileNumberFlowMode = 'edit',
  originalPhoneVerificationValue?: string
) => {
  const { nationalNumberRegex } = getSiteConfig().PHONE_NUMBER;
  const mobileStepKey = mode === 'add' ? 'add_mobile_number' : 'edit_mobile_number';
  const mobileRequiredMessageKey = mode === 'add' ? 'steps.add_mobile_number.validation.mobile_required' : 'steps.edit_mobile_number.validation.current_mobile_required';
  const mobileTrackContainer = mode === 'add' ? 'Add Mobile' : 'Edit Mobile';

  const buildNumberSchema = () => {
    if (mode === 'add') {
      return z.string().min(1, t(mobileRequiredMessageKey));
    }

    const validationMessage = t('steps.edit_mobile_number.validation.number');
    const requiredSchema = z.string().min(1, validationMessage);

    return nationalNumberRegex ? requiredSchema.regex(nationalNumberRegex, validationMessage) : requiredSchema;
  };

  const mobileSchema = z.object({
    mobileNumber: z.object({
      prefix: z.string().min(1, t(mobileRequiredMessageKey)),
      number: buildNumberSchema(),
    }),
  });

  const verificationSchema = z.object({
    verificationCode: z.string().regex(/^\d{6}$/, t('steps.verification.validation.verification_code_required')),
  });

  const confirmationSchema = z.object({});

  return [
    {
      id: 'current-mobile',
      component: (
        <EditMobileNumberStep
          namespace={`my_account.settings.edit_mobile_number.steps.${mobileStepKey}`}
          onRegisterBackHandler={registerBackHandler}
          originalPhoneVerificationValue={mode === 'edit' ? originalPhoneVerificationValue : undefined}
        />
      ),
      schema: mobileSchema,
      'data-track-container': mobileTrackContainer,
    },
    {
      id: 'verification',
      component: (
        <VerificationStep
          namespace="my_account.settings.edit_mobile_number.steps.verification"
          onRegisterBackHandler={registerBackHandler}
          onVerificationFailureDone={onVerificationFailureDone}
        />
      ),
      schema: verificationSchema,
      'data-track-container': 'Verify Mobile',
    },
    {
      id: 'confirmation',
      component: (
        <ConfirmationStep namespace="my_account.settings.edit_mobile_number.steps.confirmation" onRegisterBackHandler={registerBackHandler} onConfirmDone={onConfirmDone} />
      ),
      schema: confirmationSchema,
      'data-track-container': 'Mobile Verified',
    },
  ];
};

export { createEditMobileNumberSteps };
